import { isQuestionComponentType, parseSlide } from '@/types/slide';
import { formatName } from '@/utils/name';
import { getScore } from '@/utils/score';
import { useLazyQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import { StudentResult } from '../types/result';
import {
    GET_CLASSROOMS,
    GET_CLASSROOMS_RESULTS,
    GET_RESULT_FILES,
    GetClassroomsResponse,
    GetClassroomsResultsResponse,
    GetClassroomsResultsVariables,
    GetClassroomsVariables,
    GetResultFilesResponse,
} from './gql';

// TODO type homework
export const useMultipleStudentResults = () => {
    // classroom result gql
    const [getClassrooms] = useLazyQuery<GetClassroomsResponse, GetClassroomsVariables>(GET_CLASSROOMS, {
        fetchPolicy: 'network-only',
    });
    const [getClassroomsResults] = useLazyQuery<GetClassroomsResultsResponse, GetClassroomsResultsVariables>(GET_CLASSROOMS_RESULTS, {
        fetchPolicy: 'network-only',
    });

    const [getResultFiles] = useLazyQuery<GetResultFilesResponse>(GET_RESULT_FILES);
    const [loading, setLoading] = useState(false);

    type TestroomType = GetClassroomsResponse['testrooms'][number];

    const parseClassroomResult = useCallback(
        async (ids: string[], classrooms: GetClassroomsResponse | undefined, classroomsResults: GetClassroomsResultsResponse | undefined) => {
            if (ids.length > 0 && classrooms?.testrooms.length && classroomsResults?.results) {
                const slides = classrooms.testrooms.flatMap((testroom) => testroom.lessonVersion.slides);
                const students = classrooms.testrooms.flatMap((testroom) => testroom.class.students);
                const results = classroomsResults.results;

                if (slides && students && results) {
                    const testroomMap = new Map<string, TestroomType>();
                    classrooms.testrooms.forEach((testroom) => {
                        testroomMap.set(testroom.id, testroom);
                    });

                    let sortedSlides: (TestroomType['lessonVersion']['slides'][number] & { questionCount: number })[] = [];

                    ids.forEach((id) => {
                        const testroom = testroomMap.get(id);
                        if (testroom) {
                            const slides = testroom.lessonVersion.slides
                                .slice()
                                .sort((a, b) => a.sort - b.sort)
                                .map((slide) => {
                                    const parsedSlide = parseSlide(slide);
                                    return {
                                        ...slide,
                                        questionCount: parsedSlide.data.filter(isQuestionComponentType).length,
                                    };
                                });

                            sortedSlides = sortedSlides.concat(slides);
                        }
                    });

                    const totalQuestionCount = sortedSlides.reduce((acc, slide) => acc + slide.questionCount, 0);

                    const studentResults = students.reduce(
                        (acc, student) => {
                            // extract slides which it is from a testroom that the student is in the testroom.class
                            const studentTestrooms = classrooms.testrooms.filter((testroom) => testroom.class.students.some((s) => s.id === student.id));

                            const studentSlides = studentTestrooms
                                .flatMap((testroom) =>
                                    testroom.lessonVersion.slides.map((slide) => ({
                                        ...slide,
                                        testroomId: testroom.id,
                                        testroomName: testroom.testroomName,
                                    })),
                                )
                                .sort((a, b) => a.sort - b.sort);

                            // remove duplicates then join class names as string
                            const className = [...new Set(studentTestrooms.map((t) => t.class.name))].join(', ');

                            acc[student.id] = {
                                id: student.id,
                                studentName: formatName(student.familyName, student.givenName),
                                studentNumber: student.studentNumber,
                                className: className,
                                score: 0,
                                timeSpent: 0,
                                questionCount: totalQuestionCount,
                                responseCount: 0,
                                slides: studentSlides.map((rawSlide) => {
                                    const slide = parseSlide(rawSlide);

                                    return {
                                        ...slide,
                                        questions: slide.data.filter(isQuestionComponentType).map((question) => {
                                            const answers = [...results].filter((r) => r.slide.id === slide.id && student.id === r.student.id && question.id === r.componentId);
                                            return {
                                                ...question,
                                                answers: answers
                                                    .sort((a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime())
                                                    .map((answer) => {
                                                        return {
                                                            ...answer,
                                                            fetchResultFiles: () =>
                                                                getResultFiles({
                                                                    variables: {
                                                                        where: { result: { id: { equals: answer.id } } },
                                                                    },
                                                                }).then(async (response) => {
                                                                    return response.data?.resultFiles.map((result) => ({ url: result.file.url })) || [];
                                                                }),
                                                        };
                                                    }),
                                            };
                                        }),
                                        score: 0,
                                    };
                                }),
                            };

                            return acc;
                        },
                        {} as Record<string, StudentResult>,
                    );

                    // calculate time spent
                    for (const result of results) {
                        const student = studentResults[result.student.id];
                        if (student) {
                            student.timeSpent += new Date(result.endTime).getTime() - new Date(result.startTime).getTime();
                        }
                    }

                    // calculate score
                    for (const student of Object.values(studentResults)) {
                        for (const slide of student.slides) {
                            slide.score = slide.questions.reduce((acc, question) => {
                                return acc + getScore(question);
                            }, 0);
                        }

                        student.score = student.slides.reduce((acc, slide) => {
                            return acc + slide.score;
                        }, 0);
                    }

                    // calculate response count
                    for (const student of Object.values(studentResults)) {
                        for (const slide of student.slides) {
                            student.responseCount += slide.questions.reduce((acc, question) => {
                                return acc + (question.answers.length > 0 ? 1 : 0);
                            }, 0);
                        }
                    }

                    return {
                        results: studentResults,
                        totalQuestionCount: totalQuestionCount,
                    };
                }
            }

            return {
                results: {},
                totalQuestionCount: 0,
            };
        },
        [getResultFiles],
    );

    type FetchStudentResultsCallbackType = (type: 'classroom' | 'homework', ids: string[]) => Promise<{ results: Record<string, StudentResult>; totalQuestionCount: number }>;

    const fetchStudentResults = useCallback<FetchStudentResultsCallbackType>(
        async (type: 'classroom' | 'homework', ids: string[]) => {
            setLoading(true);
            try {
                if (ids.length === 0) {
                    return { results: {}, totalQuestionCount: 0 };
                }

                if (type === 'classroom') {
                    const [classroomsResult, classroomsResultsResult] = await Promise.all([
                        getClassrooms({ variables: { where: { id: { in: ids } } } }),
                        getClassroomsResults({ variables: { where: { testroom: { id: { in: ids } } } } }),
                    ]);

                    const classrooms = classroomsResult.data;
                    const classroomsResults = classroomsResultsResult.data;

                    return parseClassroomResult(ids, classrooms, classroomsResults);
                }
                // TODO: homework

                return {
                    results: {},
                    totalQuestionCount: 0,
                };
            } finally {
                setLoading(false);
            }
        },
        [getClassrooms, getClassroomsResults, parseClassroomResult],
    );

    return {
        loading,
        fetchStudentResults,
    };
};
